import statusEffectsService from '@/core/services/statusEffectsService';

const state = () => ({
  // Player status effects
  playerBuffs: [],
  playerDebuffs: [],
  playerBuffMultipliers: {
    xpGain: 1.0,
    coinsGain: 1.0,
    productionSpeed: 1.0,
    gymTraining: 1.0
  },

  // Fighter status effects
  fighterBuffs: {},      // { fighterId: [buffs] }
  fighterDebuffs: {},    // { fighterId: [debuffs] }
  fighterBuffedStats: {}, // { fighterId: { stats } }
  fighterDebuffedStats: {}, // { fighterId: { stats } }

  // UI state
  loading: {
    playerBuffs: false,
    playerDebuffs: false,
    fighterBuffs: false,
    fighterDebuffs: false
  },

  // Status effects configuration
  buffTypes: {
    // Player buffs
    'xp_multiplier': { name: 'Multiplicador XP', color: '#2ecc71', icon: 'trending_up' },
    'coins_multiplier': { name: 'Multiplicador Coins', color: '#f1c40f', icon: 'monetization_on' },
    'production_speed': { name: 'Velocidade Produção', color: '#3498db', icon: 'speed' },
    'gym_training': { name: 'Eficiência Treino', color: '#9b59b6', icon: 'fitness_center' },
    'business_income': { name: 'Renda Negócios', color: '#16a085', icon: 'business' },

    // Fighter buffs  
    'strength_boost': { name: 'Força', color: '#e74c3c', icon: 'fitness_center' },
    'agility_boost': { name: 'Agilidade', color: '#f39c12', icon: 'speed' },
    'defense_boost': { name: 'Defesa', color: '#3498db', icon: 'shield' },
    'stamina_boost': { name: 'Stamina', color: '#2ecc71', icon: 'battery_charging_full' },
    'attack_boost': { name: 'Ataque', color: '#e67e22', icon: 'gavel' },
    'dodge_boost': { name: 'Esquiva', color: '#9b59b6', icon: 'directions_run' }
  },

  debuffTypes: {
    // Player debuffs
    'market_crash': { name: 'Crash Mercado', color: '#e74c3c', icon: 'trending_down' },
    'reputation_loss': { name: 'Perda Reputação', color: '#c0392b', icon: 'sentiment_dissatisfied' },
    'tax_audit': { name: 'Auditoria Fiscal', color: '#8e44ad', icon: 'account_balance' },
    'supply_shortage': { name: 'Escassez', color: '#d35400', icon: 'remove_shopping_cart' },
    'economic_recession': { name: 'Recessão', color: '#2c3e50', icon: 'trending_down' },

    // Fighter debuffs
    'bleeding': { name: 'Sangramento', color: '#e74c3c', icon: 'bloodtype' },
    'poisoned': { name: 'Envenenado', color: '#9b59b6', icon: 'coronavirus' },
    'weakened': { name: 'Fraqueza', color: '#95a5a6', icon: 'sentiment_very_dissatisfied' },
    'stunned': { name: 'Atordoado', color: '#f39c12', icon: 'psychology' },
    'fatigued': { name: 'Fadiga', color: '#7f8c8d', icon: 'battery_low' },
    'diseased': { name: 'Doente', color: '#c0392b', icon: 'sick' },
    'injured': { name: 'Ferido', color: '#d68910', icon: 'healing' }
  }
});

const getters = {
  // Player getters
  activePlayerBuffs: (state) => state.playerBuffs.filter(buff => 
    new Date(buff.expiresAt) > new Date()
  ),
  
  activePlayerDebuffs: (state) => state.playerDebuffs.filter(debuff => 
    new Date(debuff.expiresAt) > new Date()
  ),

  playerStatusCount: (state, getters) => ({
    buffs: getters.activePlayerBuffs.length,
    debuffs: getters.activePlayerDebuffs.length
  }),

  // Fighter getters
  activeFighterBuffs: (state) => (fighterId) => {
    const buffs = state.fighterBuffs[fighterId] || [];
    return buffs.filter(buff => new Date(buff.expiresAt) > new Date());
  },

  activeFighterDebuffs: (state) => (fighterId) => {
    const debuffs = state.fighterDebuffs[fighterId] || [];
    return debuffs.filter(debuff => new Date(debuff.expiresAt) > new Date());
  },

  fighterStatusCount: (state, getters) => (fighterId) => ({
    buffs: getters.activeFighterBuffs(fighterId).length,
    debuffs: getters.activeFighterDebuffs(fighterId).length
  }),

  // Status effect configuration getters
  getBuffConfig: (state) => (effectType) => state.buffTypes[effectType],
  getDebuffConfig: (state) => (effectType) => state.debuffTypes[effectType],
  
  // Enhanced stats getters
  getFighterEnhancedStats: (state) => (fighterId) => {
    const buffedStats = state.fighterBuffedStats[fighterId];
    const debuffedStats = state.fighterDebuffedStats[fighterId];
    
    if (!buffedStats || !debuffedStats) return null;
    
    // Combine both buff and debuff effects
    const baseStats = buffedStats.baseStats;
    const finalStats = { ...baseStats };
    
    // Apply buff multipliers first
    Object.keys(buffedStats.multipliers).forEach(stat => {
      if (finalStats[stat]) {
        finalStats[stat] = Math.round(baseStats[stat] * buffedStats.multipliers[stat]);
      }
    });
    
    // Then apply debuff multipliers
    Object.keys(debuffedStats.multipliers).forEach(stat => {
      if (finalStats[stat]) {
        finalStats[stat] = Math.round(finalStats[stat] * debuffedStats.multipliers[stat]);
      }
    });
    
    return {
      baseStats,
      buffedStats: buffedStats.buffedStats,
      debuffedStats: debuffedStats.debuffedStats,
      finalStats,
      activeBuffs: buffedStats.activeBuffs,
      activeDebuffs: debuffedStats.activeDebuffs
    };
  }
};

const mutations = {
  // Player mutations
  SET_PLAYER_BUFFS(state, buffs) {
    state.playerBuffs = buffs;
  },

  SET_PLAYER_DEBUFFS(state, debuffs) {
    state.playerDebuffs = debuffs;
  },

  SET_PLAYER_BUFF_MULTIPLIERS(state, multipliers) {
    state.playerBuffMultipliers = { ...state.playerBuffMultipliers, ...multipliers };
  },

  ADD_PLAYER_BUFF(state, buff) {
    const existingIndex = state.playerBuffs.findIndex(b => b.id === buff.id);
    if (existingIndex >= 0) {
      state.playerBuffs.splice(existingIndex, 1, buff);
    } else {
      state.playerBuffs.push(buff);
    }
  },

  REMOVE_PLAYER_DEBUFF(state, debuffId) {
    state.playerDebuffs = state.playerDebuffs.filter(d => d.id !== debuffId);
  },

  // Fighter mutations
  SET_FIGHTER_BUFFS(state, { fighterId, buffs }) {
    state.fighterBuffs = {
      ...state.fighterBuffs,
      [fighterId]: buffs
    };
  },

  SET_FIGHTER_DEBUFFS(state, { fighterId, debuffs }) {
    state.fighterDebuffs = {
      ...state.fighterDebuffs,
      [fighterId]: debuffs
    };
  },

  SET_FIGHTER_BUFFED_STATS(state, { fighterId, stats }) {
    state.fighterBuffedStats = {
      ...state.fighterBuffedStats,
      [fighterId]: stats
    };
  },

  SET_FIGHTER_DEBUFFED_STATS(state, { fighterId, stats }) {
    state.fighterDebuffedStats = {
      ...state.fighterDebuffedStats,
      [fighterId]: stats
    };
  },

  ADD_FIGHTER_BUFF(state, { fighterId, buff }) {
    const currentBuffs = state.fighterBuffs[fighterId] || [];
    const existingIndex = currentBuffs.findIndex(b => b.id === buff.id);
    
    if (existingIndex >= 0) {
      currentBuffs.splice(existingIndex, 1, buff);
    } else {
      currentBuffs.push(buff);
    }
    
    state.fighterBuffs = {
      ...state.fighterBuffs,
      [fighterId]: [...currentBuffs]
    };
  },

  REMOVE_FIGHTER_DEBUFF(state, { fighterId, debuffId }) {
    const currentDebuffs = state.fighterDebuffs[fighterId] || [];
    state.fighterDebuffs = {
      ...state.fighterDebuffs,
      [fighterId]: currentDebuffs.filter(d => d.id !== debuffId)
    };
  },

  // Loading mutations
  SET_LOADING(state, { type, loading }) {
    state.loading[type] = loading;
  }
};

const actions = {
  // Player actions
  async fetchPlayerBuffs({ commit, rootState }) {
    if (!rootState.player.id) return;
    
    commit('SET_LOADING', { type: 'playerBuffs', loading: true });
    try {
      const buffs = await statusEffectsService.getPlayerBuffs(rootState.player.id);
      commit('SET_PLAYER_BUFFS', buffs);
    } catch (error) {
      console.error('Error fetching player buffs:', error);
    } finally {
      commit('SET_LOADING', { type: 'playerBuffs', loading: false });
    }
  },

  async fetchPlayerDebuffs({ commit, rootState }) {
    if (!rootState.player.id) return;
    
    commit('SET_LOADING', { type: 'playerDebuffs', loading: true });
    try {
      const debuffs = await statusEffectsService.getPlayerDebuffs(rootState.player.id);
      commit('SET_PLAYER_DEBUFFS', debuffs);
    } catch (error) {
      console.error('Error fetching player debuffs:', error);
    } finally {
      commit('SET_LOADING', { type: 'playerDebuffs', loading: false });
    }
  },

  async curePlayerDebuff({ commit, dispatch, rootState }, debuffId) {
    try {
      const result = await statusEffectsService.curePlayerDebuff(rootState.player.id, debuffId);
      if (result.success) {
        commit('REMOVE_PLAYER_DEBUFF', debuffId);
        
        // Update player coins
        await dispatch('player/fetchPlayerData', null, { root: true });
        
        return result;
      }
      throw new Error(result.reason);
    } catch (error) {
      console.error('Error curing player debuff:', error);
      throw error;
    }
  },

  // Fighter actions
  async fetchFighterBuffs({ commit }, fighterId) {
    commit('SET_LOADING', { type: 'fighterBuffs', loading: true });
    try {
      const buffs = await statusEffectsService.getFighterBuffs(fighterId);
      commit('SET_FIGHTER_BUFFS', { fighterId, buffs });
    } catch (error) {
      console.error('Error fetching fighter buffs:', error);
    } finally {
      commit('SET_LOADING', { type: 'fighterBuffs', loading: false });
    }
  },

  async fetchFighterDebuffs({ commit }, fighterId) {
    commit('SET_LOADING', { type: 'fighterDebuffs', loading: true });
    try {
      const debuffs = await statusEffectsService.getFighterDebuffs(fighterId);
      commit('SET_FIGHTER_DEBUFFS', { fighterId, debuffs });
    } catch (error) {
      console.error('Error fetching fighter debuffs:', error);
    } finally {
      commit('SET_LOADING', { type: 'fighterDebuffs', loading: false });
    }
  },

  async fetchFighterEnhancedStats({ commit }, fighterId) {
    try {
      const [buffedStats, debuffedStats] = await Promise.all([
        statusEffectsService.getFighterBuffedStats(fighterId),
        statusEffectsService.getFighterDebuffedStats(fighterId)
      ]);
      
      commit('SET_FIGHTER_BUFFED_STATS', { fighterId, stats: buffedStats });
      commit('SET_FIGHTER_DEBUFFED_STATS', { fighterId, stats: debuffedStats });
    } catch (error) {
      console.error('Error fetching fighter enhanced stats:', error);
    }
  },

  async cureFighterDebuff({ commit, dispatch, rootState }, { fighterId, debuffId }) {
    try {
      const result = await statusEffectsService.cureFighterDebuff(
        rootState.player.id,
        fighterId,
        debuffId
      );
      
      if (result.success) {
        commit('REMOVE_FIGHTER_DEBUFF', { fighterId, debuffId });
        
        // Update player coins and fighter stats
        await dispatch('player/fetchPlayerData', null, { root: true });
        await dispatch('fetchFighterEnhancedStats', fighterId);
        
        return result;
      }
      throw new Error(result.reason);
    } catch (error) {
      console.error('Error curing fighter debuff:', error);
      throw error;
    }
  },

  // Bulk actions
  async loadPlayerStatusEffects({ dispatch }) {
    await Promise.all([
      dispatch('fetchPlayerBuffs'),
      dispatch('fetchPlayerDebuffs')
    ]);
  },

  async loadFighterStatusEffects({ dispatch }, fighterId) {
    await Promise.all([
      dispatch('fetchFighterBuffs', fighterId),
      dispatch('fetchFighterDebuffs', fighterId),
      dispatch('fetchFighterEnhancedStats', fighterId)
    ]);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};